// src/pages/CompletedEvent.jsx
import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Chip,
  Stack,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Assets
import img1 from "../assets/img1.jpeg";
import img2 from "../assets/img2.jpeg";
import img3 from "../assets/img3.jpeg";
import Award2 from "../assets/Award2.jpeg";
import Award from "../assets/Award.jpeg";
import Award3 from "../assets/Award3.jpeg";
import img7 from "../assets/img7.jpeg";
import img8 from "../assets/img8.jpeg";
import img9 from "../assets/img9.jpeg";

const images = [
  { id: 1, name: "Team Celebration", location: "Mumbai, India", src: img1 },
  { id: 2, name: "Project Completion", location: "Pune, India", src: img2 },
  { id: 3, name: "Award Ceremony", location: "Delhi, India", src: img3 },
  { id: 4, name: "Achievement Award", location: "Bangalore, India", src: Award2 },
  { id: 5, name: "Best Performer Award", location: "Chennai, India", src: Award },
  { id: 6, name: "Leadership Excellence", location: "Hyderabad, India", src: Award3 },
  { id: 7, name: "Site Visit", location: "Ahmedabad, India", src: img7 },
  { id: 8, name: "Annual Meet", location: "Kolkata, India", src: img8 },
  { id: 9, name: "Innovation Showcase", location: "Jaipur, India", src: img9 },
];

function ImageCard({ image, onOpen }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        height: 360,
        overflow: "hidden",
        position: "relative",
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 16px 40px rgba(2, 6, 23, 0.15)",
          borderColor: "transparent",
        },
        "&:focus-within": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardActionArea
        onClick={() => onOpen(image)}
        sx={{ flexGrow: 1, alignItems: "stretch" }}
      >
        <Box sx={{ position: "relative", height: 250 }}>
          {!loaded && (
            <Skeleton
              variant="rectangular"
              animation="wave"
              sx={{ position: "absolute", inset: 0, borderRadius: 0 }}
            />
          )}
          <CardMedia
            component="img"
            image={image.src}
            alt={image.name}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            sx={{
              height: 250,
              width: "100%",
              objectFit: "cover",
              objectPosition: "center",
              opacity: loaded ? 1 : 0,
              transition: "opacity .4s ease",
            }}
          />

          {/* subtle overlay on hover */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(0,0,0,.35), rgba(0,0,0,0) 50%)",
              opacity: 0,
              transition: "opacity .25s ease",
              ".MuiCardActionArea-root:hover &": { opacity: 1 },
            }}
          />
          <Chip
            label={image.location}
            size="small"
            sx={{
              position: "absolute",
              bottom: 10,
              left: 10,
              bgcolor: "rgba(255,255,255,0.9)",
              backdropFilter: "saturate(140%) blur(4px)",
              fontWeight: 600,
            }}
          />
        </Box>

        <CardContent sx={{ py: 1.5, px: 2.25 }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 700, color: "text.primary", fontSize: "1.05rem" }}
            noWrap
            title={image.name}
          >
            {image.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {image.location}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function CompletedEvent() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const handleOpen = (img) => {
    setActive(img);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 5, md: 8 },
        px: { xs: 2, sm: 4 },
        background:
          "linear-gradient(180deg, #F7FAFF 0%, #F3F6FB 30%, #F9FBFF 100%)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          maxWidth: "1100px",
          mx: "auto",
          mb: 4,
          textAlign: "center",
        }}
      >
        <Typography
          variant="overline"
          sx={{ letterSpacing: ".16em", color: "primary.main", fontWeight: 700 }}
        >
          Our Moments
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            mt: 0.5,
            background:
              "linear-gradient(90deg, #0284c7 0%, #06b6d4 50%, #4f46e5 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Image Gallery
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", mt: 1 }}
        >
          A curated snapshot of our awards, achievements, and field moments across India.
        </Typography>

        <Stack
          direction="row"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Chip label={`${images.length} Photos`} size="small" />
          <Chip label="High Quality" size="small" variant="outlined" />
        </Stack>
      </Box>

      {/* Grid */}
      <Box sx={{ maxWidth: "1100px", mx: "auto" }}>
        <Grid container spacing={3}>
          {images.map((image) => (
            <Grid key={image.id} item xs={12} sm={6} md={4}>
              <Fade in timeout={400}>
                <div>
                  <ImageCard image={image} onOpen={handleOpen} />
                </div>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{
              bgcolor: "rgba(0,0,0,0.6)",
              color: "#fff",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {active && (
            <Box sx={{ position: "relative", bgcolor: "#000" }}>
              <Box
                component="img"
                src={active.src}
                alt={active.name}
                sx={{
                  display: "block",
                  width: "100%",
                  height: { xs: 320, sm: 480, md: 560 },
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
              />
              <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {active.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {active.location}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
























































// import React from "react";
// import { Grid, Card, CardMedia, CardContent, Typography, Box } from "@mui/material";

// import img1 from "../assets/img1.jpeg";
// import img2 from "../assets/img2.jpeg";
// import img3 from "../assets/img3.jpeg";
// import Award2 from "../assets/Award2.jpeg";
// import Award from "../assets/Award.jpeg";
// import Award3 from "../assets/Award3.jpeg";
// import img7 from "../assets/img7.jpeg";
// import img8 from "../assets/img8.jpeg";
// import img9 from "../assets/img9.jpeg";

// const images = [
//   { id: 1, name: "Team Celebration", location: "Mumbai, India", src: img1 },
//   { id: 2, name: "Project Completion", location: "Pune, India", src: img2 },
//   { id: 3, name: "Award Ceremony", location: "Delhi, India", src: img3 },
//   { id: 4, name: "Achievement Award", location: "Bangalore, India", src: Award2 },
//   { id: 5, name: "Best Performer Award", location: "Chennai, India", src: Award },
//   { id: 6, name: "Leadership Excellence", location: "Hyderabad, India", src: Award3 },
//   { id: 7, name: "Site Visit", location: "Ahmedabad, India", src: img7 },
//   { id: 8, name: "Annual Meet", location: "Kolkata, India", src: img8 },
//   { id: 9, name: "Innovation Showcase", location: "Jaipur, India", src: img9 },
// ];

// const completedevent = () => {
//   return (
//     <Box
//       sx={{
//         padding: 4,
//         backgroundColor: "#f5f5f5",
//         minHeight: "100vh",
//       }}
//     >
//       <Typography
//         variant="h4"
//         align="center"
//         fontWeight="bold"
//         gutterBottom
//         sx={{ marginBottom: 4 }}
//       >
//         Image Gallery
//       </Typography>
//       <Grid container spacing={4}>
//         {images.map((image) => (
//           <Grid item xs={12} sm={6} md={4} key={image.id}>
//             <Card
//               sx={{
//                 borderRadius: 2,
//                 boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
//                 overflow: "hidden",
//                 display: "flex",
//                 flexDirection: "column",
//                 height: 350, // Ensure all cards have the same height
//               }}
//             >
//               <CardMedia
//                 component="img"
//                 image={image.src}
//                 alt={image.name}
//                 sx={{
//                   height: 250, // Consistent height for images
//                   width: "100%", // Full width
//                   objectFit: "cover", // Ensure the image covers the area
//                   objectPosition: "center",
//                 }}
//               />
//               <CardContent sx={{ flexGrow: 1 }}>
//                 <Typography
//                   variant="h6"
//                   fontWeight="bold"
//                   sx={{ marginBottom: 1, color: "#000" }}
//                 >
//                   {image.name}
//                 </Typography>
//                 <Typography variant="body2" sx={{ color: "#555" }}>
//                   {image.location}
//                 </Typography>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>
//     </Box>
//   );
// };

// export default completedevent;
